/**
 * Pure SVG geometry helpers for the VisualFraction widget.
 */

/**
 * Converts the unified `size` unit (cell rem) to a comparable SVG viewport rem
 * for circle/triangle shapes.
 */
export const VISUAL_FRACTION_SVG_SIZE_SCALE = 8;

// ─── Circle sectors ──────────────────────────────────────────────────────────

export type CircleSectorProps =
  | { kind: 'circle' }
  | { kind: 'path'; d: string };

export function getCircleSectorProps(
  index: number,
  divisions: number,
): CircleSectorProps {
  if (divisions <= 1) return { kind: 'circle' };

  const angle = 360 / divisions;
  const startAngle = -90 + index * angle;
  const endAngle = startAngle + angle;
  const start = polarToCartesian(50, 50, 48, endAngle);
  const end = polarToCartesian(50, 50, 48, startAngle);
  const largeArcFlag = angle > 180 ? 1 : 0;

  return {
    kind: 'path',
    d: [
      'M', 50, 50,
      'L', start.x, start.y,
      'A', 48, 48, 0, largeArcFlag, 0, end.x, end.y,
      'Z',
    ].join(' '),
  };
}

export function polarToCartesian(
  cx: number,
  cy: number,
  r: number,
  angleInDegrees: number,
): { x: number; y: number } {
  const rad = (angleInDegrees * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// ─── Triangle parts ───────────────────────────────────────────────────────────

/**
 * Computes the SVG path for one triangular sub-part of a triangle shape
 * divided into `divisions` equal triangles (divisions must be a perfect square).
 */
export function getEqualTrianglePartPath(index: number, divisions: number): string {
  const side = Math.sqrt(divisions);
  if (!Number.isInteger(side)) {
    throw new Error(`getEqualTrianglePartPath: divisions (${divisions}) must be a perfect square.`);
  }

  const smallSide = 96 / side;
  const smallHeight = (smallSide * Math.sqrt(3)) / 2;
  const totalHeight = side * smallHeight;
  const offsetY = (100 - totalHeight) / 2;
  let cursor = 0;

  for (let row = 0; row < side; row++) {
    const y = offsetY + row * smallHeight;
    const rowLeft = 50 - ((row + 1) * smallSide) / 2;

    // Upright triangles in this row
    for (let col = 0; col <= row; col++) {
      if (cursor === index) {
        const x = rowLeft + col * smallSide;
        return ['M', x + smallSide / 2, y, 'L', x + smallSide, y + smallHeight, 'L', x, y + smallHeight, 'Z'].join(' ');
      }
      cursor++;
    }

    // Inverted triangles in this row
    for (let col = 0; col < row; col++) {
      if (cursor === index) {
        const x = rowLeft + col * smallSide;
        return ['M', x + smallSide, y + smallHeight, 'L', x + smallSide / 2, y, 'L', x + smallSide * 1.5, y, 'Z'].join(' ');
      }
      cursor++;
    }
  }

  return '';
}

// ─── Shared layout helpers ────────────────────────────────────────────────────

export function isPerfectSquare(value: number): boolean {
  return Number.isInteger(Math.sqrt(value));
}

export function getGridColumnCount(divisions: number): number {
  return Math.ceil(Math.sqrt(divisions));
}
